import { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, FileAudio, X, Play, Pause, Scissors, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { getQalunSurahPages } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

// Function to actually trim audio and create a new blob
const trimAudioFile = async (audioUrl, startTime, endTime) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  
  // Fetch the audio file
  const response = await fetch(audioUrl);
  const arrayBuffer = await response.arrayBuffer();
  
  // Decode the audio
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  
  // Calculate sample positions
  const sampleRate = audioBuffer.sampleRate;
  const startSample = Math.floor(startTime * sampleRate);
  const endSample = Math.floor(endTime * sampleRate);
  const trimmedLength = endSample - startSample;
  
  // Create a new buffer for trimmed audio
  const trimmedBuffer = audioContext.createBuffer(
    audioBuffer.numberOfChannels,
    trimmedLength,
    sampleRate
  );
  
  // Copy the trimmed portion
  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
    const sourceData = audioBuffer.getChannelData(channel);
    const targetData = trimmedBuffer.getChannelData(channel);
    for (let i = 0; i < trimmedLength; i++) {
      targetData[i] = sourceData[startSample + i];
    }
  }
  
  // Convert to WAV blob
  const wavBlob = audioBufferToWav(trimmedBuffer);
  
  audioContext.close();
  
  return wavBlob;
};

// Convert AudioBuffer to WAV blob
const audioBufferToWav = (buffer) => {
  const numChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const format = 1; // PCM
  const bitDepth = 16;
  
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numChannels * bytesPerSample;
  
  const dataLength = buffer.length * blockAlign;
  const bufferLength = 44 + dataLength;
  
  const arrayBuffer = new ArrayBuffer(bufferLength);
  const view = new DataView(arrayBuffer);
  
  // Write WAV header
  const writeString = (offset, string) => {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  };
  
  writeString(0, 'RIFF');
  view.setUint32(4, bufferLength - 8, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, format, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);
  writeString(36, 'data');
  view.setUint32(40, dataLength, true);
  
  // Write audio data
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < numChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
      const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, intSample, true);
      offset += 2;
    }
  }
  
  return new Blob([arrayBuffer], { type: 'audio/wav' });
};

// Simple audio editor with clickable progress bar
const SimpleAudioEditor = ({ audioUrl, onTrimComplete }) => {
  const { t } = useLanguage();
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setEndTime(audio.duration);
    };

    const handleTimeUpdate = () => {
      if (!isDragging) {
        setCurrentTime(audio.currentTime);
      }
      if (audio.currentTime >= endTime) {
        audio.currentTime = startTime;
      }
    };

    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [startTime, endTime, isDragging]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      if (audio.currentTime < startTime || audio.currentTime >= endTime) {
        audio.currentTime = startTime;
      }
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleProgressClick = (e) => {
    if (!progressRef.current || !audioRef.current || duration === 0) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = rect.right - e.clientX;
    const percentage = clickX / rect.width;
    const seekTime = percentage * duration;
    
    audioRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const handleProgressDrag = (e) => {
    if (!isDragging || !progressRef.current || duration === 0) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = Math.max(0, Math.min(rect.right - e.clientX, rect.width));
    const percentage = clickX / rect.width;
    const seekTime = percentage * duration;
    
    setCurrentTime(seekTime);
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    handleProgressClick(e);
  };

  const handleDragEnd = () => {
    if (isDragging && audioRef.current) {
      audioRef.current.currentTime = currentTime;
    }
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleProgressDrag(e);
      const handleMouseUp = () => handleDragEnd();
      
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, currentTime]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSave = () => {
    onTrimComplete({
      startTime,
      endTime,
      duration: endTime - startTime,
    });
  };

  const setStartToCurrent = () => {
    if (currentTime < endTime - 0.5) {
      setStartTime(currentTime);
    }
  };

  const setEndToCurrent = () => {
    if (currentTime > startTime + 0.5) {
      setEndTime(currentTime);
    }
  };

  return (
    <div className="space-y-4">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Clickable progress bar */}
      <div 
        ref={progressRef}
        className="relative h-20 bg-muted rounded-xl overflow-hidden cursor-pointer select-none"
        onMouseDown={handleDragStart}
        data-testid="audio-progress-bar"
      >
        {/* Selection zone */}
        <div 
          className="absolute top-0 bottom-0 bg-primary/30"
          style={{
            right: `${(startTime / duration) * 100}%`,
            width: `${((endTime - startTime) / duration) * 100}%`,
          }}
        />
        
        {/* Start marker (green) */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-green-500"
          style={{ right: `${(startTime / duration) * 100}%` }}
        />
        
        {/* End marker (red) */}
        <div 
          className="absolute top-0 bottom-0 w-1 bg-red-500"
          style={{ right: `${(endTime / duration) * 100}%` }}
        />
        
        {/* Current position cursor */}
        <div 
          className="absolute top-0 bottom-0 w-0.5 bg-foreground z-10"
          style={{ right: `${(currentTime / duration) * 100}%` }}
        >
          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-foreground rounded-full" />
          <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-foreground rounded-full" />
        </div>

        {/* Time indicator */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs bg-background/90 px-2 py-1 rounded font-mono">
          {formatTime(currentTime)}
        </div>
      </div>

      {/* Time display */}
      <div className="flex justify-between text-sm">
        <span className="text-green-500 font-medium">{t('startTime')}: {formatTime(startTime)}</span>
        <span className="text-foreground font-medium">
          {t('duration')}: {formatTime(endTime - startTime)}
        </span>
        <span className="text-red-500 font-medium">{t('endTime')}: {formatTime(endTime)}</span>
      </div>

      {/* Quick set buttons */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={setStartToCurrent}
          className="flex-1 rounded-full text-green-500 border-green-500/50 hover:bg-green-500/10"
          data-testid="set-start-btn"
        >
          {t('setStartHere')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={setEndToCurrent}
          className="flex-1 rounded-full text-red-500 border-red-500/50 hover:bg-red-500/10"
          data-testid="set-end-btn"
        >
          {t('setEndHere')}
        </Button>
      </div>

      {/* Fine-tune sliders */}
      <div className="space-y-2">
        <Label className="text-green-500">{t('startTime')}: {formatTime(startTime)}</Label>
        <Slider
          value={[startTime]}
          min={0}
          max={duration}
          step={0.1}
          onValueChange={([val]) => {
            if (val < endTime - 0.5) setStartTime(val);
          }}
        />
      </div>

      <div className="space-y-2">
        <Label className="text-red-500">{t('endTime')}: {formatTime(endTime)}</Label>
        <Slider
          value={[endTime]}
          min={0}
          max={duration}
          step={0.1}
          onValueChange={([val]) => {
            if (val > startTime + 0.5) setEndTime(val);
          }}
        />
      </div>

      {/* Playback controls */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={togglePlay}
          className="rounded-full h-12 w-12"
          data-testid="editor-play-btn"
        >
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
        </Button>

        <Button
          onClick={handleSave}
          className="flex-1 rounded-full bg-primary hover:bg-primary/90 gap-2"
          data-testid="editor-trim-btn"
        >
          <Scissors className="h-4 w-4" />
          {t('saveExcerpt')}
        </Button>
      </div>
    </div>
  );
};

export const ExcerptModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  surahId,
  surahNumber,
  excerpt = null,
  mode = 'create',
  isLoading = false 
}) => {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);

  const [pageNumber, setPageNumber] = useState(excerpt?.page_number || 1);
  const [mushafPageNumber, setMushafPageNumber] = useState(excerpt?.mushaf_page_number || '');
  const [surahPages, setSurahPages] = useState([]);
  const [title, setTitle] = useState(excerpt?.title || '');
  const [hizb, setHizb] = useState(excerpt?.hizb || 0);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [trimData, setTrimData] = useState(null);
  const [step, setStep] = useState(mode === 'edit' ? 'info' : 'upload');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState('idle'); // 'idle' | 'uploading' | 'converting' | 'done' | 'error'
  const [uploadError, setUploadError] = useState(null);
  const xhrRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit') {
        setStep('info');
      } else if (mode === 'editAudio' && excerpt?.audio_file_id) {
        // Load existing audio for editing
        setStep('loading');
        const loadExistingAudio = async () => {
          try {
            const audioUrl = `${process.env.REACT_APP_BACKEND_URL}/api/audio/${excerpt.audio_file_id}`;
            const response = await fetch(audioUrl);
            const blob = await response.blob();
            const file = new File([blob], excerpt.title || 'audio.mp3', { type: blob.type });
            const url = URL.createObjectURL(blob);
            setAudioFile(file);
            setAudioUrl(url);
            setStep('edit');
          } catch (error) {
            console.error('Error loading audio:', error);
            setStep('upload');
          }
        };
        loadExistingAudio();
      } else {
        setStep('upload');
      }
      
      if (excerpt) {
        setPageNumber(excerpt.page_number || 1);
        setMushafPageNumber(excerpt.mushaf_page_number || '');
        setTitle(excerpt.title || '');
      } else {
        setPageNumber(1);
        setMushafPageNumber('');
        setTitle('');
      }
    }
  }, [isOpen, mode, excerpt]);

  useEffect(() => {
    if (!isOpen || !surahNumber) return;
    let active = true;
    getQalunSurahPages(surahNumber)
      .then(({ data }) => {
        if (!active) return;
        const pages = data.pages || [];
        setSurahPages(pages);
        const mappedPage = pages[(excerpt?.page_number || 1) - 1];
        if (excerpt?.mushaf_page_number) {
          setMushafPageNumber(String(excerpt.mushaf_page_number));
        } else if (mappedPage) {
          setMushafPageNumber(String(mappedPage));
        }
      })
      .catch((error) => {
        console.error('Failed to load Qalun page mapping:', error);
      });
    return () => { active = false; };
  }, [isOpen, surahNumber, excerpt?.page_number, excerpt?.mushaf_page_number]);

  useEffect(() => {
    if (!isOpen) {
      if (xhrRef.current) {
        try { cancelUpload(); } catch (e) { /* noop */ }
        xhrRef.current = null;
      }
      if (audioUrl) URL.revokeObjectURL(audioUrl);
      setAudioFile(null);
      setAudioUrl(null);
      setTrimData(null);
      setUploadProgress(0);
      setUploadStage('idle');
      setUploadError(null);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, []);

  // Detect video files by MIME type or extension (some iOS files may have empty MIME)
  const isVideoFile = (file) => {
    if (!file) return false;
    if (file.type && file.type.startsWith('video/')) return true;
    const name = (file.name || '').toLowerCase();
    return /\.(mp4|mov|m4v|qt|mkv|avi|webm|wmv|flv|mpeg|mpg|3gp|3g2)$/.test(name);
  };

  const isAudioFile = (file) => {
    if (!file) return false;
    if (file.type && file.type.startsWith('audio/')) return true;
    const name = (file.name || '').toLowerCase();
    return /\.(mp3|m4a|wav|aac|ogg|flac|opus|wma)$/.test(name);
  };

  const processSelectedFile = (file) => {
    if (!file) return;
    const video = isVideoFile(file);
    const audio = isAudioFile(file);
    if (!video && !audio) return;

    if (audioUrl) URL.revokeObjectURL(audioUrl);

    if (video) {
      // Upload video to backend for FFmpeg conversion, then load the resulting MP3
      // into the trim editor so the admin can still trim before saving.
      uploadAndConvertVideo(file);
    } else {
      setAudioFile(file);
      const url = URL.createObjectURL(file);
      setAudioUrl(url);
      setStep('edit');
    }
  };

  const uploadAndConvertVideo = async (file) => {
    setStep('converting');
    setUploadProgress(0);
    setUploadStage('uploading');
    setUploadError(null);

    // Chunked upload (5 MB chunks) to bypass Cloudflare / ingress body-size limits.
    const CHUNK_SIZE = 5 * 1024 * 1024;
    const total = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const abortRef = { aborted: false, xhr: null, uploadId: null };
    xhrRef.current = abortRef;

    try {
      // 1. Init
      const token = localStorage.getItem('quran-player-admin-token');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
      const initResp = await fetch(`${apiBase}/api/upload/init`, { method: 'POST', headers: authHeaders });
      if (!initResp.ok) throw new Error(`init failed: ${initResp.status}`);
      const { upload_id: uploadId } = await initResp.json();
      abortRef.uploadId = uploadId;

      // 2. Send each chunk
      for (let i = 0; i < total; i += 1) {
        if (abortRef.aborted) throw new Error('aborted');
        const start = i * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const blob = file.slice(start, end);

        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          abortRef.xhr = xhr;
          xhr.open('POST', `${apiBase}/api/upload/${uploadId}/chunk`, true);
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const overall = ((i + e.loaded / e.total) / total) * 100;
              setUploadProgress(Math.min(100, Math.round(overall)));
            }
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              setUploadProgress(Math.round(((i + 1) / total) * 100));
              resolve();
            } else {
              reject(new Error(`chunk ${i} HTTP ${xhr.status}`));
            }
          };
          xhr.onerror = () => reject(new Error(`chunk ${i} network error`));
          xhr.onabort = () => reject(new Error('aborted'));

          const fd = new FormData();
          fd.append('index', String(i));
          fd.append('total', String(total));
          fd.append('chunk', blob, `chunk_${i}`);
          xhr.send(fd);
        });
      }

      // 3. Finalize → server reassembles + FFmpeg-converts, returns MP3 blob
      if (abortRef.aborted) throw new Error('aborted');
      setUploadProgress(100);
      setUploadStage('converting');

      const finalizeBlob = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        abortRef.xhr = xhr;
        xhr.open('POST', `${apiBase}/api/upload/${uploadId}/finalize-mp3`, true);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.responseType = 'blob';
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.response);
          else reject(new Error(`finalize HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('finalize network error'));
        xhr.onabort = () => reject(new Error('aborted'));
        const fd = new FormData();
        fd.append('filename', file.name || 'upload.mp4');
        xhr.send(fd);
      });

      xhrRef.current = null;
      const baseName = (file.name || 'audio').replace(/\.[^.]+$/, '');
      const mp3File = new File([finalizeBlob], `${baseName}.mp3`, { type: 'audio/mpeg' });
      const url = URL.createObjectURL(finalizeBlob);
      setAudioFile(mp3File);
      setAudioUrl(url);
      setUploadStage('done');
      setStep('edit');
    } catch (err) {
      xhrRef.current = null;
      if (abortRef.uploadId && !abortRef.aborted) {
        // Best-effort cleanup of server-side temp chunks on error
        fetch(`${apiBase}/api/upload/${abortRef.uploadId}`, { method: 'DELETE', headers: authHeaders }).catch(() => {});
      }
      if (err && err.message === 'aborted') {
        setUploadStage('idle');
        setStep('upload');
      } else {
        setUploadError(err && err.message ? err.message : 'Erreur');
        setUploadStage('error');
      }
    }
  };

  const cancelUpload = () => {
    const ref = xhrRef.current;
    if (!ref) return;
    ref.aborted = true;
    if (ref.xhr) {
      try { ref.xhr.abort(); } catch (e) { /* noop */ }
    }
    if (ref.uploadId) {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/${ref.uploadId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('quran-player-admin-token') || ''}` } }).catch(() => {});
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    processSelectedFile(file);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    processSelectedFile(file);
  };

  const handleTrimComplete = (data) => {
    setTrimData(data);
    setStep('info');
  };

  const [isTrimming, setIsTrimming] = useState(false);

  const handleSave = async () => {
    const formData = new FormData();
    formData.append('surah_id', surahId);
    if (!Number.isInteger(Number(mushafPageNumber)) || Number(mushafPageNumber) < 1) {
      setUploadError(t('invalidMushafPage'));
      setStep('info');
      return;
    }
    formData.append('page_number', pageNumber.toString());
    formData.append('mushaf_page_number', String(mushafPageNumber));
    formData.append('title', title);
    formData.append('hizb', hizb.toString());
    
    // If we have audio and trim data, actually trim the audio
    if (audioFile && trimData && audioUrl) {
      try {
        setIsTrimming(true);
        console.log('Trimming audio from', trimData.startTime, 'to', trimData.endTime);
        
        const trimmedBlob = await trimAudioFile(audioUrl, trimData.startTime, trimData.endTime);
        const trimmedFile = new File([trimmedBlob], audioFile.name.replace(/\.[^.]+$/, '.wav'), { 
          type: 'audio/wav' 
        });
        
        formData.append('audio_file', trimmedFile);
        // Set times to 0 since the file is now trimmed
        formData.append('start_time', '0');
        formData.append('end_time', trimData.duration.toString());
        formData.append('duration', trimData.duration.toString());
        
        console.log('Audio trimmed successfully, size:', trimmedBlob.size);
      } catch (error) {
        console.error('Error trimming audio:', error);
        // Fallback to original behavior
        formData.append('audio_file', audioFile);
        formData.append('start_time', (trimData?.startTime || 0).toString());
        formData.append('end_time', (trimData?.endTime || 0).toString());
        formData.append('duration', (trimData?.duration || 0).toString());
      } finally {
        setIsTrimming(false);
      }
    } else if (audioFile) {
      formData.append('audio_file', audioFile);
      formData.append('start_time', (trimData?.startTime || 0).toString());
      formData.append('end_time', (trimData?.endTime || 0).toString());
      formData.append('duration', (trimData?.duration || 0).toString());
    } else {
      // No new audio, just update metadata
      formData.append('start_time', (trimData?.startTime || 0).toString());
      formData.append('end_time', (trimData?.endTime || 0).toString());
      formData.append('duration', (trimData?.duration || 0).toString());
    }

    onSave(formData, excerpt?.id);
  };

  const handleRemoveAudio = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioFile(null);
    setAudioUrl(null);
    setTrimData(null);
    setStep('upload');
  };

  const getTitle = () => {
    if (mode === 'replace') return t('replaceAudio');
    if (mode === 'editAudio') return t('adjustAudio');
    if (mode === 'edit') return t('editExcerpt');
    return t('addExcerpt');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto" data-testid="excerpt-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {getTitle()}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('addExcerpt')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* Loading state */}
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">{t('loadingAudio')}</p>
            </div>
          )}

          {/* Converting state: uploading video then waiting for server FFmpeg */}
          {step === 'converting' && (
            <div
              className="flex flex-col items-center justify-center py-10 gap-5"
              dir="rtl"
              data-testid="video-converting-step"
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                {uploadStage === 'error' ? (
                  <X className="h-8 w-8 text-red-500" />
                ) : (
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                )}
              </div>

              <div className="text-center space-y-1">
                <p className="font-medium text-foreground">
                  {uploadStage === 'uploading' && 'جارٍ رفع الفيديو...'}
                  {uploadStage === 'converting' && 'جارٍ تحويل الفيديو إلى MP3...'}
                  {uploadStage === 'done' && 'تم!'}
                  {uploadStage === 'error' && 'حدث خطأ أثناء الرفع'}
                </p>
                {uploadStage === 'uploading' && (
                  <p className="text-sm text-muted-foreground" data-testid="upload-progress-text">
                    {uploadProgress}%
                  </p>
                )}
                {uploadStage === 'converting' && (
                  <p className="text-sm text-muted-foreground">
                    قد يستغرق ذلك بضع ثوانٍ حسب حجم الملف
                  </p>
                )}
                {uploadStage === 'error' && uploadError && (
                  <p className="text-sm text-red-500">{uploadError}</p>
                )}
              </div>

              {/* Progress bar */}
              <div
                className="w-full h-3 bg-muted rounded-full overflow-hidden"
                data-testid="upload-progress-bar"
              >
                <div
                  className={`h-full transition-all duration-200 ${
                    uploadStage === 'error' ? 'bg-red-500' : 'bg-primary'
                  } ${uploadStage === 'converting' ? 'animate-pulse' : ''}`}
                  style={{
                    width:
                      uploadStage === 'converting' || uploadStage === 'done'
                        ? '100%'
                        : `${uploadProgress}%`,
                  }}
                />
              </div>

              <Button
                variant="outline"
                onClick={() => {
                  cancelUpload();
                  setStep('upload');
                  setUploadStage('idle');
                  setUploadError(null);
                }}
                className="rounded-full"
                data-testid="upload-cancel-btn"
              >
                {uploadStage === 'error' ? t('cancel') : 'إلغاء'}
              </Button>
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-border rounded-2xl p-12
                flex flex-col items-center justify-center gap-4
                cursor-pointer hover:border-primary/50 hover:bg-muted/50
                transition-all duration-200"
              data-testid="audio-dropzone"
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Upload className="h-8 w-8 text-primary" />
              </div>
              <div className="text-center">
                <p className="font-medium text-foreground">{t('selectAudio')}</p>
                <p className="text-sm text-muted-foreground mt-1">{t('dropAudio')}</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*,video/*,.mp3,.m4a,.wav,.aac,.ogg,.mp4,.mkv,.avi,.mov,audio/mpeg,audio/mp4,video/mp4"
                onChange={handleFileChange}
                className="hidden"
                data-testid="audio-file-input"
              />
            </div>
          )}

          {/* Step 2: Edit */}
          {step === 'edit' && audioUrl && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                <FileAudio className="h-5 w-5 text-primary" />
                <span className="flex-1 truncate text-sm">{audioFile?.name}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleRemoveAudio}
                  className="h-8 w-8 rounded-full"
                  data-testid="remove-audio-btn"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <SimpleAudioEditor
                audioUrl={audioUrl}
                onTrimComplete={handleTrimComplete}
              />
            </div>
          )}

          {/* Step 3: Info & Save */}
          {step === 'info' && (
            <div className="space-y-6">
              {audioFile && (
                <div className="flex items-center gap-3 p-3 bg-muted rounded-xl">
                  <FileAudio className="h-5 w-5 text-primary" />
                  <span className="flex-1 truncate text-sm">{audioFile.name}</span>
                  {audioUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setStep('edit')}
                      className="rounded-full"
                    >
                      {t('audioEditor')}
                    </Button>
                  )}
                </div>
              )}

              {trimData && (
                <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 rounded-xl text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">{t('startTime')}</p>
                    <p className="font-mono">{trimData.startTime.toFixed(1)}s</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('endTime')}</p>
                    <p className="font-mono">{trimData.endTime.toFixed(1)}s</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t('duration')}</p>
                    <p className="font-mono">{trimData.duration.toFixed(1)}s</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="title">{t('titleOptional')}</Label>
                <Input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('titlePlaceholder')}
                  className="rounded-xl"
                  data-testid="title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="page_number">{t('wajhNumber')}</Label>
                  <Input
                    id="page_number"
                    type="number"
                    min="1"
                    value={pageNumber}
                    onChange={(e) => {
                      const nextPage = parseInt(e.target.value, 10) || 1;
                      setPageNumber(nextPage);
                      if (surahPages[nextPage - 1]) setMushafPageNumber(String(surahPages[nextPage - 1]));
                    }}
                    className="rounded-xl"
                    data-testid="page-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mushaf_page_number">{t('mushafPageNumber')}</Label>
                  <Input
                    id="mushaf_page_number"
                    type="number"
                    min="1"
                    max="612"
                    value={mushafPageNumber}
                    onChange={(e) => setMushafPageNumber(e.target.value)}
                    className="rounded-xl"
                    data-testid="mushaf-page-number-input"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">{t('mushafPageHint')}</p>
              {uploadError === t('invalidMushafPage') && (
                <p role="alert" className="text-sm text-destructive">{uploadError}</p>
              )}

              <div className="space-y-2">
                <Label htmlFor="hizb">الحزب</Label>
                <Input
                  id="hizb"
                  type="number"
                  min="0"
                  max="60"
                  value={hizb}
                  onChange={(e) => setHizb(parseInt(e.target.value, 10) || 0)}
                  placeholder="0 = غير محدد"
                  className="rounded-xl"
                  data-testid="hizb-input"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1 rounded-full"
                  data-testid="excerpt-cancel-btn"
                  disabled={isTrimming || isLoading}
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isLoading || isTrimming || (!audioFile && mode !== 'edit' && mode !== 'editAudio')}
                  className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                  data-testid="excerpt-save-btn"
                >
                  {isTrimming ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('trimming')}
                    </>
                  ) : isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('loading')}
                    </>
                  ) : (
                    t('save')
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
